import { motion } from 'motion/react'
import type { CustomerOrder } from '../../customerOrdersApi'
import type { ShopLanguage, ShopLocale, ShopMessageKey } from '../../../i18n'
import { customerMotionTransition } from '../customer-area'
import {
  resolveTimelineProgressPercent,
  toStepState,
  type CustomerOrderDetailLabels,
  type OrderTimelineStepId,
} from './orderDetailShared'
import { toOrderStatusLabel } from '../../helpers/status'
import { formatDateTime } from '../../helpers/formatters'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type CustomerOrderStatusSectionProps = {
  orderDetail: CustomerOrder
  language: ShopLanguage
  locale: ShopLocale
  labels: CustomerOrderDetailLabels
  reducedMotion: boolean
  t: TranslateFn
}

const TIMELINE_STEPS: Array<{ id: OrderTimelineStepId; labelKey: keyof CustomerOrderDetailLabels; dateKey: keyof CustomerOrder }> =
  [
    { id: 'created', labelKey: 'stepCreated', dateKey: 'createdAt' },
    { id: 'paid', labelKey: 'stepPaid', dateKey: 'paidAt' },
    { id: 'shipped', labelKey: 'stepShipped', dateKey: 'shippedAt' },
  ]

export const CustomerOrderStatusSection = ({
  orderDetail,
  language,
  locale,
  labels,
  reducedMotion,
  t,
}: CustomerOrderStatusSectionProps) => {
  const createdStatusDescription =
    orderDetail.paymentState === 'pending'
      ? language === 'it'
        ? 'Pagamento in attesa di conferma. Completa il checkout per confermare l ordine.'
        : 'Payment is pending confirmation. Complete checkout to confirm your order.'
      : orderDetail.paymentState === 'failed'
        ? language === 'it'
          ? 'Ultimo tentativo di pagamento non riuscito. Riprova dal checkout.'
          : 'The last payment attempt failed. Retry from checkout.'
        : orderDetail.paymentState === 'unpaid'
          ? language === 'it'
            ? 'Pagamento non completato. Procedi al checkout per confermare l ordine.'
            : 'Payment not completed. Continue to checkout to confirm your order.'
          : labels.statusDescriptionCreated

  const statusDescription =
    orderDetail.status === 'created'
      ? createdStatusDescription
      : orderDetail.status === 'paid'
        ? labels.statusDescriptionPaid
        : orderDetail.status === 'shipped'
          ? labels.statusDescriptionShipped
          : labels.statusDescriptionCancelled

  return (
    <section className="ca-card-shell ca-theme-transition rounded-xl px-5 py-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-(--color-text-primary)">{labels.statusTitle}</h3>
          <p className="mt-0.5 text-xs text-(--color-text-secondary)">{labels.statusSubtitle}</p>
        </div>
        <div className="max-w-[21rem] text-right">
          <p className="text-[11px] uppercase tracking-wide text-(--color-text-muted)">{labels.statusCurrent}</p>
          <p className="mt-0.5 text-sm font-semibold text-(--color-text-primary)">{toOrderStatusLabel(orderDetail.status, language)}</p>
          <p className="mt-1 text-xs text-(--color-text-secondary)">{statusDescription}</p>
        </div>
      </div>

      <div className="relative mt-4 hidden sm:block">
        <div className="h-1 rounded-full bg-(--color-border-subtle)" />
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full bg-(--color-action-primary)"
          initial={{ width: 0 }}
          animate={{ width: `${resolveTimelineProgressPercent(orderDetail)}%` }}
          transition={customerMotionTransition}
        />
      </div>

      <ol className="mt-3 grid gap-3 sm:grid-cols-3">
        {TIMELINE_STEPS.map((step, index) => {
          const state = toStepState(orderDetail, step.id)
          const cardClass =
            state === 'completed'
              ? 'border-(--color-border-default) bg-(--color-bg-surface-muted)'
              : state === 'current'
                ? 'border-(--color-status-warning-border) bg-(--color-status-warning-bg) shadow-(--shadow-sm) ring-1 ring-[color-mix(in_srgb,var(--color-status-warning-border)_55%,transparent)]'
                : 'border-(--color-border-default) bg-[color-mix(in_srgb,var(--color-bg-surface-muted)_82%,transparent)]'
          const dotClass =
            state === 'completed'
              ? 'border-(--color-status-neutral) bg-(--color-status-neutral)'
              : state === 'current'
                ? 'border-(--color-status-warning) bg-(--color-status-warning) ring-4 ring-[color-mix(in_srgb,var(--color-status-warning-border)_55%,transparent)]'
                : 'border-(--color-border-strong) bg-(--color-bg-card)'
          const titleClass =
            state === 'completed'
              ? 'text-(--color-text-primary)'
              : state === 'current'
                ? 'text-(--color-text-primary)'
                : 'text-(--color-text-muted)'
          const dateClass =
            state === 'completed'
              ? 'text-(--color-text-secondary)'
              : state === 'current'
                ? 'text-(--color-text-secondary)'
                : 'text-(--color-text-muted)'
          const dateValue = orderDetail[step.dateKey] as string | null

          return (
            <motion.li
              key={step.id}
              initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
              animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
              transition={{ ...customerMotionTransition, delay: reducedMotion ? 0 : index * 0.03 }}
              className={`rounded-xl border px-3.5 py-3 ${cardClass}`}
            >
              <div className="flex items-start gap-2.5">
                <motion.span
                  className={`mt-0.5 h-3.5 w-3.5 shrink-0 rounded-full border-2 ${dotClass}`}
                  animate={state === 'current' && !reducedMotion ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                  transition={{ duration: 0.2, ease: customerMotionTransition.ease }}
                />
                <div className="min-w-0">
                  <p className={`text-sm font-semibold ${titleClass}`}>{labels[step.labelKey]}</p>
                  <p className={`mt-1 text-xs ${dateClass}`}>{formatDateTime(dateValue, locale, t('common.never'))}</p>
                </div>
              </div>
            </motion.li>
          )
        })}
      </ol>

      {orderDetail.status === 'cancelled' ? (
        <p className="mt-3 rounded-lg border border-(--color-status-error-border) bg-(--color-status-error-bg) px-3 py-2 text-sm text-(--color-status-error)">
          {labels.statusCancelled}
        </p>
      ) : null}
    </section>
  )
}
